/**
 * Frontend Application Entry Point
 *
 * Initializes React application, sets up providers, and renders root component.
 * Configures Auth0 for authentication, React Query for API state management,
 * and React Router for navigation. Includes ErrorBoundary for crash recovery
 * and Toaster for user notifications.
 * Shows a branded AuthTransition overlay while the Auth0 SDK is restoring
 * session state on page load.
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { Auth0Provider } from "@auth0/auth0-react";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import { ApiProvider } from "./components/ApiProvider";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthLoadingGate } from "./components/AuthLoadingGate";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Toaster } from "./components/ui/sonner";
import { auth0Config } from "./config/auth0";
import { AuthenticationError } from "./services/api";
import "./index.css";
import { registerServiceWorker } from "./registerServiceWorker";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry auth failures — they're permanent, not transient
        if (error instanceof AuthenticationError) return false;
        return failureCount < 1;
      },
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {/* Outermost so the error boundary fallback and the auth loading gate are
        themed too — both can render before anything else mounts. */}
    <ThemeProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <Auth0Provider {...auth0Config}>
            <QueryClientProvider client={queryClient}>
              <AuthLoadingGate>
                <ApiProvider>
                  <App />
                  <Toaster />
                </ApiProvider>
              </AuthLoadingGate>
            </QueryClientProvider>
          </Auth0Provider>
        </BrowserRouter>
      </ErrorBoundary>
    </ThemeProvider>
  </React.StrictMode>
);

// Registers the service worker and reloads when a new one takes control.
registerServiceWorker();
